import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core'
import { NavigationService } from '../../core/services/navigation.service'
import { AuthService } from '../../modules/auth/auth.service'
import { AppUser, AppUserRole } from '../../../../domain/user/app-user.model'
import { AsyncPipe, Location, NgIf } from '@angular/common'
import { Router, RouterLink, RouterLinkActive } from '@angular/router'
import { concatMap, mergeMap } from 'rxjs/operators'
import { MatDialog } from '@angular/material/dialog'
import { SettingsEditDialog } from 'src/app/shared/settings/settings-edit.dialog'
import { Observable } from 'rxjs'
import { ShopSettingsService } from '../../modules/auth/shop-settings.service'
import { DestroyAware } from '../../shared/utils/destroy-aware.decorator'
import { ShopService } from '../../core/services/shop.service'
import { ShoppingCartDialog } from '../../shared/shopping-cart/shopping-cart.dialog'
import { CartService } from 'src/app/modules/dashboard/shop/services/cart.service'
import { NotificationPanelDialog } from 'src/app/shared/notifications/notification-panel.dialog'
import { NotificationsFirestoreService } from 'src/app/core/firestore-services/notifications-firestore.service'
import { AppUsersFirestoreService } from 'src/app/core/firestore-services/app-users-firestore.service'
import { SnackbarService } from 'src/app/core/services/snackbar.service'
import { LetDirective } from '../../shared/modules/light-shared/let.directive'
import { MatToolbar } from '@angular/material/toolbar'
import { HasAnyRoleDirective } from '../../shared/auth/has-any-role.directive'
import { MatIconButton } from '@angular/material/button'
import { MatTooltip } from '@angular/material/tooltip'
import { MatIcon } from '@angular/material/icon'
import { MatBadge } from '@angular/material/badge'
import { LanguageSwitchComponent } from './language-switch/language-switch.component'
import { TranslatePipe } from '@ngx-translate/core'

@DestroyAware()
@Component({
	selector: 'app-navbar',
	templateUrl: './navbar.component.html',
	styleUrls: ['./navbar.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		LetDirective,
		MatToolbar,
		NgIf,
		HasAnyRoleDirective,
		RouterLinkActive,
		RouterLink,
		MatIconButton,
		MatTooltip,
		MatIcon,
		MatBadge,
		LanguageSwitchComponent,
		AsyncPipe,
		TranslatePipe,
	],
})
export class NavbarComponent {
	private readonly firestoreService = inject<NotificationsFirestoreService>(NotificationsFirestoreService)
	private readonly navigationService = inject<NavigationService>(NavigationService)
	private readonly snackbarService = inject<SnackbarService>(SnackbarService)
	private readonly authService = inject<AuthService>(AuthService)
	private readonly location = inject<Location>(Location)
	private readonly dialog = inject<MatDialog>(MatDialog)
	readonly router = inject<Router>(Router)
	private readonly cdf = inject<ChangeDetectorRef>(ChangeDetectorRef)
	private readonly shopSettingsService = inject(ShopSettingsService)
	private readonly shopService = inject(ShopService)
	readonly cartService = inject(CartService)
	private readonly appUsersFirestoreService = inject(AppUsersFirestoreService)

	readonly user$: Observable<AppUser | null> = this.authService.appUser$
	readonly logoUrl1$: Observable<string> = this.shopSettingsService.logoUrl1$
	readonly isPublicShop$: Observable<boolean> = this.shopService.isPublicShop$
	readonly notificationsBadge$: Observable<number> = this.firestoreService.getNewNotificationsCount()
	readonly Role = AppUserRole
	searchInput: string = ''
	bSideMenu: boolean = false
	hideNotifications: boolean = false
	hideShoppingCart: boolean = true

	constructor() {
		this.authService.appUserChanges$.subscribe((user) => {
			if (user) {
				;[AppUserRole.Seller].includes(user?.role) ? (this.hideNotifications = true) : null
				user.email === 'demo.customyour.shoes@gmail.com'
					? (this.hideShoppingCart = true)
					: (this.hideShoppingCart = false) // Close your eyes
			}
		})
	}

	search(): void {
		const route = this.router.url.substring(1)
		if (route.includes('shop')) {
			if (this.searchInput && this.searchInput.trim() !== '') {
				this.router.navigate([route], { queryParams: { q: this.searchInput.trim() } })
			} else {
				this.router.navigate([route])
			}
		} else {
			if (this.searchInput && this.searchInput.trim() !== '') {
				this.router.navigate(['/dashboard'], { queryParams: { q: this.searchInput.trim() } })
			} else {
				this.router.navigate(['dashboard'])
			}
		}
	}

	editSettings(): void {
		this.closeMenuMobile()
		this.showSettingsDialog()
	}

	shoppingCart(): void {
		this.closeMenuMobile()
		this.showCartDialog()
	}

	notificationsPanel(): void {
		this.closeMenuMobile()
		this.showNotificationsDialog()
	}

	setSearchInput(event: any): void {
		this.searchInput = event.target.value
	}

	goToFinancialOrders(): void {
		this.navigationService.goToFinancialOrders()
	}

	goToFinancialInvoices(): void {
		this.navigationService.goToFinancialInvoices()
	}

	goToBusinessClientOrders(): void {
		this.navigationService.goToBusinessClientOrders()
	}

	goToBusinessClientSellers(): void {
		this.navigationService.goToBusinessClientSellers()
	}

	goToBusinessClientShoes(): void {
		this.navigationService.goToBusinessClientShoes()
	}

	goToLogin(): void {
		this.navigationService.goToLogin()
	}

	goBack(): void {
		this.location.back()
	}

	login(): void {
		this.goToLogin()
	}

	logout(): void {
		this.closeMenuMobile()
		this.shopSettingsService.currentUserShopSettings$.subscribe((settings) => {
			this.authService.logout(true, settings?.url)
		})
	}

	closeMenuMobile(): void {
		this.bSideMenu = false
	}

	onLogoClick(): void {
		this.navigationService.goToDashboard().subscribe()
	}

	private showSettingsDialog(): void {
		this.shopSettingsService.currentUserShopSettings$
			.pipe(
				mergeMap((settings) =>
					this.dialog
						.open(SettingsEditDialog, {
							data: settings,
							width: '550px',
							autoFocus: false,
							disableClose: true,
						})
						.afterClosed(),
				),
			)
			.subscribe()
	}

	private showCartDialog(): void {
		const dialogRef = this.dialog.open(ShoppingCartDialog, {
			width: '700px',
			autoFocus: false,
		})

		dialogRef.afterClosed().subscribe((message) => {
			if (message) {
				this.snackbarService.showMessage(message)
			}
		})
	}

	private showNotificationsDialog(): void {
		this.authService.appUser$
			.pipe(
				concatMap((loggedUser) => {
					const user = { uid: loggedUser?.uid, lastCheck: new Date().toISOString() } as AppUser
					this.appUsersFirestoreService.update(user)
					return this.dialog
						.open(NotificationPanelDialog, {
							width: '600px',
							autoFocus: false,
						})
						.afterClosed()
				}),
			)
			.subscribe()
	}
}
